import React from 'react';
import { createPortal } from 'react-dom';

// Cartellino 70 x 50 mm con codice a barre EAN-13.
// Lo stesso componente <Cartellino> disegna sia l'anteprima (in scala) sia le pagine di stampa,
// quindi ciò che vedi è ciò che esce dalla stampante. Tutte le misure sono in mm.

export const LARGHEZZA_MM = 70;
export const ALTEZZA_MM = 50;

// Il nome si rimpicciolisce se è lungo e occupa al massimo 2 righe
const dimensioneNome = (nome) => {
  const n = String(nome ?? '').length;
  if (n <= 22) return 4.2;
  if (n <= 36) return 3.6;
  if (n <= 52) return 3.1;
  return 2.7;
};

/**
 * dati: { nome, voci: string[] }  (le voci sono SAP, ubicazione, etichetta, scorta minima ...)
 * svg:  markup SVG del codice a barre (da useBarcodeEan13)
 */
export const Cartellino = ({ dati, svg }) => (
  <div
    className="cartellino"
    style={{
      width: `${LARGHEZZA_MM}mm`,
      height: `${ALTEZZA_MM}mm`,
      boxSizing: 'border-box',
      padding: '2.5mm 3mm',
      background: '#ffffff',
      color: '#000000',
      fontFamily: 'Arial, Helvetica, sans-serif',
      display: 'flex',
      flexDirection: 'column',
      gap: '1mm',
      overflow: 'hidden',
    }}
  >
    {/* codice a barre */}
    <div
      style={{ flex: '0 0 21mm', height: '21mm', width: '100%', display: 'flex', justifyContent: 'center' }}
      dangerouslySetInnerHTML={{
        __html: svg
          ? svg.replace('<svg', '<svg style="width:100%;height:100%;display:block"')
          : '',
      }}
    />

    {/* nome articolo: max 2 righe */}
    <div
      style={{
        fontSize: `${dimensioneNome(dati.nome)}mm`,
        lineHeight: 1.12,
        fontWeight: 800,
        textTransform: 'uppercase',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
        wordBreak: 'break-word',
        flex: '0 0 auto',
      }}
    >
      {dati.nome}
    </div>

    {/* voci facoltative */}
    {dati.voci.length > 0 && (
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignContent: 'flex-start',
          gap: '0.4mm 2.2mm',
          fontSize: '2.8mm',
          lineHeight: 1.15,
          overflow: 'hidden',
          flex: '1 1 auto',
          minHeight: 0,
        }}
      >
        {dati.voci.map((voce) => (
          <span
            key={voce}
            style={{ maxWidth: '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600 }}
          >
            {voce}
          </span>
        ))}
      </div>
    )}
  </div>
);

/** Anteprima in scala: stesso markup della stampa, ingrandito con transform. */
export const CartellinoAnteprima = ({ dati, svg, scala = 1.4 }) => (
  <div
    style={{ width: `${LARGHEZZA_MM * scala}mm`, height: `${ALTEZZA_MM * scala}mm`, maxWidth: '100%' }}
    className="shadow-xl border border-border/60 bg-white overflow-hidden"
  >
    <div style={{ transform: `scale(${scala})`, transformOrigin: 'top left', width: `${LARGHEZZA_MM}mm`, height: `${ALTEZZA_MM}mm` }}>
      <Cartellino dati={dati} svg={svg} />
    </div>
  </div>
);

const CSS_STAMPA = `
  #cartellini-stampa { display: none; }

  @media print {
    @page { size: ${LARGHEZZA_MM}mm ${ALTEZZA_MM}mm; margin: 0; }

    html, body {
      margin: 0 !important;
      padding: 0 !important;
      background: #ffffff !important;
      overflow: visible !important;
      height: auto !important;
    }

    /* si stampano SOLO i cartellini: tutto il resto della pagina sparisce */
    body > *:not(#cartellini-stampa) { display: none !important; }

    #cartellini-stampa { display: block !important; }

    #cartellini-stampa .cartellino-pagina {
      width: ${LARGHEZZA_MM}mm;
      height: ${ALTEZZA_MM}mm;
      overflow: hidden;
      page-break-after: always;
      break-after: page;
      page-break-inside: avoid;
      break-inside: avoid;
    }
    #cartellini-stampa .cartellino-pagina:last-child {
      page-break-after: auto;
      break-after: auto;
    }
  }
`;

/**
 * Area di stampa: un cartellino per pagina (interruzione di pagina tra le copie), con @page 70x50 mm
 * e margini 0. Va montata solo mentre il modale è aperto; sullo schermo resta nascosta.
 */
export const AreaStampaCartellini = ({ dati, svg, copie }) => createPortal(
  <div id="cartellini-stampa" aria-hidden="true">
    <style>{CSS_STAMPA}</style>
    {Array.from({ length: Math.max(1, copie) }, (_, i) => (
      <div key={i} className="cartellino-pagina">
        <Cartellino dati={dati} svg={svg} />
      </div>
    ))}
  </div>,
  document.body,
);

/** Apre la finestra di stampa del browser (l'area di stampa dei cartellini deve essere montata). */
export const stampaCartellini = () => {
  window.print();
};
